import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { projectSlice } from '../../redux/ProjectSlice';
import { LAYOUT_CONSTANTS } from '../../utils/layout-constants';

type DrawerContext = 'codeEditor' | 'dashboard' | 'pathBuilder';

export interface LeftDrawerLayoutState {
  leftDrawer: boolean;
  leftDrawerContentExpanded: boolean;
  leftDrawerWidth: number;
  leftDrawerContentWidth: number;
  toggleLeftDrawer?: () => void;
}

export function useLeftDrawerLayout(context: DrawerContext): LeftDrawerLayoutState {
  const dispatch = useAppDispatch();

  const editorOpenDrawer = useAppSelector((s) => s.project.loadedProject?.editorOpenDrawer ?? false);
  const selectedEditorDrawerTab = useAppSelector((s) => s.project.loadedProject?.selectedEditorDrawerTab ?? null);

  const dashboardOpenDrawer = useAppSelector((s) => s.project.dashboardOpenDrawer ?? false);
  const selectedDashboardTab = useAppSelector((s) => s.project.loadedProject?.selectedTabs?.dashboardTab ?? null);

  const pathBuilderOpenDrawer = useAppSelector((s) => s.project.pathBuilderOpenDrawer ?? false);
  const storedWidths = useAppSelector((s) => s.project.leftDrawerWidths || {});

  const toggleEditor = useCallback(() => dispatch(projectSlice.actions.toggleEditorOpenDrawer()), [dispatch]);
  const toggleDashboard = useCallback(() => dispatch(projectSlice.actions.toggleDashboardOpenDrawer()), [dispatch]);
  const togglePathBuilder = useCallback(() => dispatch(projectSlice.actions.togglePathBuilderOpenDrawer()), [dispatch]);

  const state = useMemo<LeftDrawerLayoutState>(() => {
    const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

    switch (context) {
      case 'codeEditor': {
        const leftDrawer = editorOpenDrawer;
        const leftDrawerContentExpanded = leftDrawer && selectedEditorDrawerTab !== null;
        const limits = LAYOUT_CONSTANTS.LEFT_DRAWER_LIMITS.CODE_EDITOR;
        const requestedWidth = storedWidths.codeEditor ?? LAYOUT_CONSTANTS.LEFT_DRAWER_DEFAULTS.CODE_EDITOR;
        const contentDefault = clamp(requestedWidth, limits.MIN, limits.MAX);
        return {
          leftDrawer,
          leftDrawerContentExpanded,
          leftDrawerWidth: LAYOUT_CONSTANTS.LEFT_DRAWER_TOTAL_WIDTH(contentDefault),
          leftDrawerContentWidth: contentDefault,
          toggleLeftDrawer: toggleEditor,
        };
      }
      case 'dashboard': {
        const leftDrawer = dashboardOpenDrawer;
        const leftDrawerContentExpanded = leftDrawer && selectedDashboardTab !== null;
        const limits = LAYOUT_CONSTANTS.LEFT_DRAWER_LIMITS.DASHBOARD;
        const requestedWidth = storedWidths.dashboard ?? LAYOUT_CONSTANTS.LEFT_DRAWER_DEFAULTS.DASHBOARD;
        const contentDefault = clamp(requestedWidth, limits.MIN, limits.MAX);
        return {
          leftDrawer,
          leftDrawerContentExpanded,
          leftDrawerWidth: LAYOUT_CONSTANTS.LEFT_DRAWER_TOTAL_WIDTH(contentDefault),
          leftDrawerContentWidth: contentDefault,
          toggleLeftDrawer: toggleDashboard,
        };
      }
      case 'pathBuilder': {
        const leftDrawer = pathBuilderOpenDrawer;
        // PathBuilder has no tab selection gating; treat drawer open as content expanded
        const leftDrawerContentExpanded = leftDrawer;
        const limits = LAYOUT_CONSTANTS.LEFT_DRAWER_LIMITS.PATH_BUILDER;
        const requestedWidth = storedWidths.pathBuilder ?? LAYOUT_CONSTANTS.LEFT_DRAWER_DEFAULTS.PATH_BUILDER;
        const contentDefault = clamp(requestedWidth, limits.MIN, limits.MAX);
        return {
          leftDrawer,
          leftDrawerContentExpanded,
          leftDrawerWidth: LAYOUT_CONSTANTS.LEFT_DRAWER_TOTAL_WIDTH(contentDefault),
          leftDrawerContentWidth: contentDefault,
          toggleLeftDrawer: togglePathBuilder,
        };
      }
    }
  }, [
    context,
    editorOpenDrawer,
    selectedEditorDrawerTab,
    dashboardOpenDrawer,
    selectedDashboardTab,
    pathBuilderOpenDrawer,
    toggleEditor,
    toggleDashboard,
    togglePathBuilder,
    storedWidths.codeEditor,
    storedWidths.dashboard,
    storedWidths.pathBuilder,
  ]);

  return state;
}
